import { useEffect, useRef, useState } from "react";
import classNames from "classnames";
import Slider, { Settings } from 'react-slick';

import LiveTicker from "./live-ticker";
import { useScreenMediaQuery } from "../../../utils/use-screen-media-query";
import { Dictionary } from "../../../types/working-model";
import './events-calendar.scss';
import { niceTwoDigitTime } from "../../../utils/utils";
import { useTypedSelector } from "../../../store/reducers/use-typed-selector";
import { EditorSizes } from "../../../types/template-layouts";
import { useHistory, useParams } from "react-router-dom";
import { ParamsProps } from "../../live-event/live-event";
import handleSessionCardClick from "../../../utils/handle-session-card-click";

export const BLOCK_SPACING = 5;
export const BLOCK_HEIGHT = 100 + BLOCK_SPACING * 2;
export const COLLAPSED_HEIGHT = 20;

interface ICalEvent extends Dictionary {
	timestamp: number;
	end_timestamp: number;
}

interface IEventsCalendarProps<T> {
	uniqueKey?: string | number;
	sessions: any[]; /* I'm not sure how to make this generic work properly so it properly extends the sessions data, so I have to use any */
	renderCard: ({ session }: { session: T; }) => JSX.Element;
}

const EventsCalendar = ({ uniqueKey = "id", sessions = [], renderCard }: IEventsCalendarProps<ICalEvent>): JSX.Element => {

	const { isLessThan1024 } = useScreenMediaQuery();
	const history = useHistory();
	const { language, eventName, uuid: paramsUuid }: ParamsProps = useParams();

	const editorSize = useTypedSelector(state => state?.CreateEventReducer?.editorSize);
	const validSessions = useTypedSelector(state => state.LiveEventReducer.validSessions);
	const finishedRegistering = useTypedSelector(state => state.LiveEventReducer.finishedRegistering);
	const userVerified = useTypedSelector(state => state.LiveEventReducer.userVerified);
	const firesidesHostSessions = useTypedSelector(state => state.LiveEventReducer.firesidesHostSessions);
	const paidSessions = useTypedSelector(state => state.LiveEventReducer.paidSessions);
	const eventBundle = useTypedSelector(state => state.LiveEventReducer.eventBundle);
	const workingEvent = useTypedSelector(state => state.CreateEventReducer.workingEvent);

	const event = eventBundle ?? workingEvent;

	const isEditorLessThan1024 = [EditorSizes.tablet, EditorSizes.mobile].includes(editorSize as EditorSizes);

	const [blocks, setBlocks] = useState<Map<number, any[]>>(new Map());
	const [sortedByStartTime, setSortedByStartTime] = useState<Record<string | number, any>[]>([]);
	const [activeId, setActiveId] = useState<null | number | string>(null);

	const calendarContainerRef = useRef<HTMLDivElement | null>(null);

	useEffect(() => {
		// do not allow a session to go into the next day (but keep the real end time for rendering dates to screen)
		// end_timestamp_display is used for calculating card heights
		const trimmedSessionTimes = sessions.map(session => {
			const _session = { ...session };
			_session.end_timestamp_display = _session.end_timestamp;
			// if sesion goes past midnight (and session is longer than 24 hours), then set end_timestamp_display to be midnight
			const dayOfStartTime = new Date(_session.timestamp).setHours(0, 0, 0, 0);
			const dayOfEndTime = new Date(_session.end_timestamp).setHours(0, 0, 0, 0);
			const longerThan24Hours = (_session.end_timestamp - _session.timestamp) / (1000 * 60 * 60) > 24;
			if (dayOfStartTime !== dayOfEndTime && longerThan24Hours) {
				const nextDay = new Date(_session.timestamp).setDate(new Date(_session.timestamp).getDate() + 1);
				const midnightNextDay = new Date(nextDay).setHours(0, 0, 0, 0);
				_session.end_timestamp_display = midnightNextDay;
			}

			return _session;
		});

		// sort by timestamp
		const _sortedByStartTime = trimmedSessionTimes.sort((a, b) => {
			// if start time matches, then sort by duration
			if (a.timestamp === b.timestamp) {
				return b.end_timestamp - a.end_timestamp;
			}
			return a.timestamp - b.timestamp;
		});
		setSortedByStartTime(_sortedByStartTime);
	}, [sessions]);

	useEffect(() => {
		// chunk each session by the hour
		if (sortedByStartTime.length) {
			const earliest = sortedByStartTime[0];
			const lastSession = sortedByStartTime[sortedByStartTime.length - 1];
			let latestEnd = lastSession.end_timestamp_display;
			// get the floor hour of earliest
			const earliestStart = earliest.timestamp;

			// get the ceiling hour of latest
			sortedByStartTime.forEach(session => {
				if (session.end_timestamp_display > latestEnd) {
					latestEnd = session.end_timestamp_display;
				}
			});

			const sessionsAdded: Record<string, any> = {};

			const _blocks: Map<number, any[]> = new Map();

			for (
				let i = earliestStart;
				i >= earliestStart && i < latestEnd;
				i = i + 1000 * 60 * 60
			) {
				const _date = new Date(i);
				_date.setMinutes(0);
				_date.setSeconds(0);
				const bottomHour = _date.getTime();
				const _bottomDate = new Date(bottomHour);

				_blocks.set(bottomHour, []);
				for (let j = 0; j < sortedByStartTime.length; j++) {
					const session = sortedByStartTime[j];

					if (sessionsAdded[session[uniqueKey]]) continue;

					const _startDate = new Date(session.timestamp);

					const startTimeDay = _startDate.getDay();
					const bottomHourDay = _bottomDate.getDay();
					const startTimeMonth = _startDate.getMonth();
					const bottomHourMonth = _bottomDate.getMonth();
					const startTimeYear = _startDate.getFullYear();
					const bottomHourYear = _bottomDate.getFullYear();
					const startTimeHours = _startDate.getHours();
					const bottomHourHours = _bottomDate.getHours();

					if (
						startTimeDay === bottomHourDay &&
						startTimeHours === bottomHourHours &&
						startTimeMonth === bottomHourMonth &&
						startTimeYear === bottomHourYear
					) {
						const block = _blocks.get(bottomHour);
						if (block) {
							block?.push(session);
							_blocks.set(bottomHour, block);
						}
						sessionsAdded[session[uniqueKey]] = session;
					}
				}
			}

			// add an extra block at the end so if we have overlap it
			// doesn't end up in no mans land
			const lastBlock = Array.from(_blocks.entries());
			const lastHour = lastBlock[lastBlock.length - 1][0];
			_blocks.set(new Date(lastHour + 1000 * 60 * 60).getTime(), []);

			setBlocks(_blocks);
		}
	}, [sortedByStartTime, uniqueKey]);

	const handleMouseOver = (id: number) => () => {
		setActiveId(id);
	};

	const handleMouseLeave = () => {
		setActiveId(null);
	};

	const renderBlockSessions = (blockSessions: any[]) => {
		return blockSessions.map((session) => {
			const startTime = new Date(session.timestamp);
			const endTime = new Date(session.end_timestamp_display);
			const startMinutes = startTime.getMinutes();
			let cardHeight = ((session.end_timestamp_display - session.timestamp) / (1000 * 60)) * (BLOCK_HEIGHT / 60);
			// calculate the start top position of the session (then add additional spacing later)
			let topPos = (BLOCK_HEIGHT / 60) * startMinutes;

			// in order to keep a gap between the session card and the hour block
			// we need to check if the card starts on the hour, if so, then reduce card height by BLOCK_SPACING
			// we need to check if the card ends on the next hour, if so, then reduce card height by an additional BLOCK_SPACING
			if (startTime.getMinutes() === 0) {
				cardHeight -= BLOCK_SPACING;
				topPos += BLOCK_SPACING;
			}
			if (endTime.getMinutes() === 0) {
				cardHeight -= BLOCK_SPACING;
			}

			if (isLessThan1024 || isEditorLessThan1024) {
				topPos -= BLOCK_SPACING;
			}

			const _style: React.CSSProperties = {
				top: `${topPos}px`,
				height: activeId === session[uniqueKey] ? "fit-content" : cardHeight + "px",
				minHeight: activeId === session[uniqueKey] ? cardHeight + "px" : "auto"
			};

			const _classNames = classNames("session-card-container", {
				'single-session': blockSessions.length === 1,
				'multi-sessions': blockSessions.length > 1,
				'active': activeId === session[uniqueKey],
			});

			// if using slick slider, we need to wrap in an extra div
			if (isLessThan1024 || isEditorLessThan1024) {
				return (
					<div key={session[uniqueKey]}>
						<div
							onMouseEnter={handleMouseOver(session[uniqueKey])}
							onTouchStart={handleMouseOver(session[uniqueKey])}
							onMouseLeave={handleMouseLeave}
							style={_style}
							className={_classNames}
							onClick={() => handleSessionCardClick({
								event,
								workingEvent,
								session,
								language,
								eventName,
								validSessions,
								finishedRegistering,
								userVerified,
								firesidesHostSessions,
								paidSessions,
								paramsUuid,
								history,
							})}
						>
							{renderCard({ session })}
						</div>
					</div>
				);
			}

			return (
				<div
					key={session[uniqueKey]}
					onMouseEnter={handleMouseOver(session[uniqueKey])}
					onTouchStart={handleMouseOver(session[uniqueKey])}
					onMouseLeave={handleMouseLeave}
					style={_style}
					className={_classNames}
					onClick={() => handleSessionCardClick({
						event,
						workingEvent,
						session,
						language,
						eventName,
						validSessions,
						finishedRegistering,
						userVerified,
						firesidesHostSessions,
						paidSessions,
						paramsUuid,
						history,
					})}
				>
					{renderCard({ session })}
				</div>
			);
		});
	};

	const sliderSettings: (data: { sessions: any[] }) => Settings = ({ sessions }) => ({
		autoplay: false,
		infinite: false,
		initialSlide: 0,
		arrows: false,
		slidesToShow: sessions.length === 1 ? 1 : 1.07,
	});

	return (
		<div
			ref={calendarContainerRef}
			className={classNames("calendar-container", { 'tablet-down': isLessThan1024 || isEditorLessThan1024 })}
		>

			{/* these side overlays are used for visual purposes for tablet/mobile only */}
			{/* because slick slider has overlay issues, we need to set the .slick-list container to overflow: visible; (x/y does not work properly) */}
			{/* however, this creates an issue where the slides are now visible outside of the left/right container, */}
			{/* so we need to use these overlays, positioned absolutely, to cover the left/right sides of the calendar */}
			<div className="calendar-side-overlay left" />
			<div className="calendar-side-overlay right" />

			{Array.from(blocks).map((entry, idx, arr) => {
				const [blockDate, blockSessions] = entry;

				const hasPastBlockOverlap = checkIfBlockHasPastSessionOverlap(entry, idx, arr);

				const activeHoverBlock = blockSessions.find(s => s[uniqueKey] === activeId);

				const isCollapsed = !hasPastBlockOverlap && !blockSessions.length;
				const height = isCollapsed ? COLLAPSED_HEIGHT + "px" : `${BLOCK_HEIGHT}px`;

				return (
					<div
						style={{
							// if last index AND no overlap from a prev session AND no sessions in the block, then display none
							display: idx === arr.length - 1 && isCollapsed
								? "none"
								: "flex"
						}}
						key={blockDate}
						className={classNames("block-container", { 'active': !!activeHoverBlock, 'first-block': idx === 0 })}
					>
						<LiveTicker
							entry={entry}
							isCollapsed={isCollapsed}
						/>
						{idx === 0 ? null : <div className="time-separator" />}
						<div
							style={{ height }}
							className="block-time-container"
						>
							{niceTwoDigitTime(blockDate)}
						</div>

						<div
							style={{ height }}
							className="block-sessions-container"
						>
							{isLessThan1024 || isEditorLessThan1024 ? (
								<Slider {...sliderSettings({ sessions: blockSessions })}>
									{renderBlockSessions(blockSessions)}
								</Slider>
							) : (
								renderBlockSessions(blockSessions)
							)}
						</div>
					</div>
				);
			})}
			<div className="block-container">
				<div className="time-separator final" />
			</div>
		</div>
	);
};

export default EventsCalendar;

const checkIfBlockHasPastSessionOverlap = (entry: [number, any[]], idx: number, arr: [number, any[]][]) => {
	const [blockDate, blockSessions] = entry;

	// determine whether or not the block should be "collapsed"
	let hasPastBlockOverlap = idx === 0 ? true : false;
	if (idx > 0 && !blockSessions.length) {
		for (let i = 0; i < arr.length; i++) {
			if (i >= idx) break;
			const _sessions = arr[i][1];
			for (let j = 0; j < _sessions.length; j++) {
				const _session = _sessions[j];
				if (
					blockDate >= _session.timestamp &&
					blockDate < _session.end_timestamp
				) {
					hasPastBlockOverlap = true;
					break;
				}
			}
			if (hasPastBlockOverlap) break;
		}
	}
	return hasPastBlockOverlap;
};